import { ZoneType } from '../types/map';
import { OSMElement } from './overpass';

function osmLanduseToZone(tags: Record<string, string>): ZoneType {
  const landuse = tags.landuse;
  const leisure = tags.leisure;

  if (landuse === 'commercial' || landuse === 'retail') return 'commercial';
  if (landuse === 'residential') return 'residential';
  if (landuse === 'industrial') return 'industrial';
  if (landuse === 'military') return 'civic';
  if (landuse === 'recreation_ground') return 'park';
  if (leisure === 'park' || leisure === 'garden') return 'park';
  return 'residential'; // default
}

export function classifyZones(landuse: OSMElement[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const element of landuse) {
    if (!element.geometry || element.geometry.length < 3) continue;
    const tags = element.tags || {};
    const zoneType = osmLanduseToZone(tags);

    // Close the polygon ring if not already closed
    const coords: [number, number][] = element.geometry.map(p => [p.lon, p.lat]);
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coords.push([...first] as [number, number]);
    }

    features.push({
      type: 'Feature',
      properties: {
        id: `zone-${element.id}`,
        zoneType,
        name: tags.name || '',
      },
      geometry: {
        type: 'Polygon',
        coordinates: [coords],
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

export function classifyNeighborhoods(neighborhoods: OSMElement[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  const seenNames = new Set<string>();

  for (const element of neighborhoods) {
    const tags = element.tags || {};
    if (!tags.name) continue;

    // Skip historic districts that aren't real neighborhoods
    if (tags.boundary === 'historic' || tags.historic) continue;

    // Deduplicate — prefer the first occurrence (nodes are queried first)
    const nameLower = tags.name.toLowerCase();
    if (seenNames.has(nameLower)) continue;
    seenNames.add(nameLower);

    // Point nodes — most neighborhoods in OSM are just named points
    if (element.type === 'node' && element.lat != null && element.lon != null) {
      features.push({
        type: 'Feature',
        properties: { id: `neighborhood-${element.id}`, name: tags.name },
        geometry: { type: 'Point', coordinates: [element.lon, element.lat] },
      });
      continue;
    }

    // Relations with geometry members
    if (element.type === 'relation' && element.members) {
      const outerRings: [number, number][][] = [];
      for (const member of element.members) {
        if (member.role === 'outer' && member.geometry) {
          const ring: [number, number][] = member.geometry.map(p => [p.lon, p.lat]);
          if (ring.length >= 3) {
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
              ring.push([...first] as [number, number]);
            }
            outerRings.push(ring);
          }
        }
      }
      if (outerRings.length > 0) {
        features.push({
          type: 'Feature',
          properties: { id: `neighborhood-${element.id}`, name: tags.name },
          geometry: { type: 'Polygon', coordinates: outerRings },
        });
      }
    }
    // Ways with direct geometry
    else if (element.geometry && element.geometry.length >= 3) {
      const coords: [number, number][] = element.geometry.map(p => [p.lon, p.lat]);
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([...first] as [number, number]);
      }
      features.push({
        type: 'Feature',
        properties: { id: `neighborhood-${element.id}`, name: tags.name },
        geometry: { type: 'Polygon', coordinates: [coords] },
      });
    }
  }

  return { type: 'FeatureCollection', features };
}
